import { useEffect, useState } from 'react';
import './draft-view.styles.scss';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowDown, faArrowUp, faXmark } from '@fortawesome/free-solid-svg-icons';
import { DRAFT_ROUNDS, LOTTERY_PICKS, draftQueueOpen, rookieContract } from '@shared/draft.js';
import { formatMoney, moneyRules } from '@shared/contracts.js';
import { listenToDoc } from '@/utils/getDataFromDb';
import { UseCloudFunction } from '@/utils/firebase.utils';
import { positions } from '@/utils/positions';
import { useSortable } from '@/utils/useSortable';
import SortTh from '@/components/SHARED/sort-th/sort-th.component';
import TeamBadge from '@/components/SHARED/team-badge/team-badge.component';
import { ModalAlert } from '@/components/modal/modal.component';
import ThreeDotsLoader from '@/components/loader/three-dots-loader.component';

// Prospect table rating columns (visible ratings only); Ovr is shown right
// after the position so it fits on a phone screen.
const COLUMNS = [
    { key: 'ovr', label: 'Ovr', get: c => c.overall },
    { key: 'off', label: 'Off', get: c => c.off_quality },
    { key: 'def', label: 'Def', get: c => c.def_quality },
    { key: 'sta', label: 'Sta', get: c => c.stamina },
    { key: '3pt', label: '3PT', get: c => c.off_3pt },
    { key: 'mid', label: 'Mid', get: c => c.off_shooting },
    { key: 'drv', label: 'Drive', get: c => c.off_drive },
    { key: 'pas', label: 'Pass', get: c => c.off_passing },
    { key: 'reb', label: 'Reb', get: c => Math.round((c.off_rebounds + c.def_rebounds) / 2) },
    { key: 'per', label: 'Perim D', get: c => c.def_speed },
    { key: 'int', label: 'Inside D', get: c => c.def_paint },
    { key: 'stl', label: 'Stl', get: c => c.def_steals },
    { key: 'blk', label: 'Blk', get: c => c.def_blocks },
];

const posText = (c) => positions[c.pos1 - 1]?.short + (c.pos2 ? `/${positions[c.pos2 - 1]?.short}` : '');
const height = (inches) => `${Math.floor(inches / 12)}'${inches % 12}"`;
const pct = (x) => `${(x * 100).toFixed(1)}%`;

// The pro draft: the lottery, the order (then the picks), the prospects
// and the manager's draft queue. myTeam: the manager's team document;
// without it (admins) the page is read-only.
const DraftView = ({ league, teams, teamLink, playerLink, myTeam }) => {

    const [ draft, setDraft ] = useState(undefined);
    // Unsaved queue edits; null shows the saved queue.
    const [ queue, setQueue ] = useState(null);
    const [ pos, setPos ] = useState(0);
    const [ busy, setBusy ] = useState(false);
    const [ alertMessage, setAlertMessage ] = useState(null);
    const sorter = useSortable();

    useEffect(() => {
        let unsub;
        listenToDoc({ 'path': `leagues/${league.id}/summary/draft`, 'callback': setDraft, 'handleListener': (u) => { unsub = u; } });
        return () => { if (unsub) unsub(); }
    }, [league.id])

    if (league.type !== 'pro') return <div className='g-card'><p className='meta'>College leagues have no draft.</p></div>;
    if (draft === undefined) return <div className='center-col'><ThreeDotsLoader /></div>;
    if (!draft) {
        return (
            <div className='g-card'>
                <h3>Draft</h3>
                <p className='meta'>The draft lottery is held when the season goes into the record books. Teams that miss the playoffs get chances at the top {LOTTERY_PICKS} picks, and the worse the record, the better the chances.</p>
            </div>
        );
    }

    const rules = moneyRules(league.settings);
    const open = draftQueueOpen(league) && !draft.done;
    const myId = myTeam?.id;
    const saved = myTeam?.draftQueue ?? [];
    const list = (queue ?? saved).filter(id => draft.prospects[id]);
    const dirty = queue !== null && queue.join() !== saved.join();
    const myPicks = draft.order.filter(o => o.teamId === myId);
    const perRound = draft.order.length / DRAFT_ROUNDS;
    const drafted = new Map(draft.order.filter(o => o.playerId).map(o => [o.playerId, o]));

    const prospects = sorter.apply(Object.values(draft.prospects)
        .filter(c => !pos || c.pos1 === pos || c.pos2 === pos)
        .sort((a, b) => b.overall - a.overall || a.lName.localeCompare(b.lName)), {
        name: c => c.lName, pos: c => c.pos1, age: c => c.age, ht: c => c.height,
        pick: c => drafted.get(c.id)?.pick ?? null,
        ...Object.fromEntries(COLUMNS.map(col => [col.key, col.get])),
    });
    const th = (k, label, first, className = 'num') => <SortTh k={k} label={label} sorter={sorter} first={first} className={className} />;

    const edit = (next) => setQueue(next);
    const move = (i, d) => {
        const next = [...list];
        [next[i], next[i + d]] = [next[i + d], next[i]];
        edit(next);
    };

    async function save() {
        setBusy(true);
        const res = await UseCloudFunction('saveDraftQueue', { leagueId: league.id, queue: list });
        setBusy(false);
        if (res.error) {
            setAlertMessage(res.error);
            return;
        }
        setQueue(null);
    }

    const name = (c) => `${c.fName} ${c.lName}`;
    const teamCell = (id) => {
        const t = teams?.[id];
        return <span className='draft-team'><TeamBadge team={t} size='sm' /> <Link to={teamLink(id)}>{t?.name ?? id}</Link></span>;
    };

    return (
        <div className='draft-view'>
            <div className='g-card'>
                <h3>Season {draft.year} draft</h3>
                <p>{draft.done ?
                    `The draft is done: ${draft.order.length} picks in ${DRAFT_ROUNDS} rounds. Undrafted prospects joined the free agent pool.` :
                    open ? 'The lottery is in. Rank the prospects you want in your draft queue; on draft day every pick is made at once.' :
                    'Draft day is next.'}</p>
                {myTeam && myPicks.length > 0 &&
                    <p className='meta'>Your picks: {myPicks.map(o => `#${o.pick}`).join(' and ')}.
                        {rules && !draft.done && ` Rookie contracts: ${myPicks.map(o => {
                            const c = rookieContract(o.pick, perRound, rules);
                            return `${formatMoney(c.salary)} for ${c.years} years`;
                        }).join('; ')}.`}
                    </p>
                }
            </div>

            {myTeam && open &&
                <div className='g-card draft-queue'>
                    <h3>Your draft queue</h3>
                    <p className='meta'>At each of your picks you get the highest player on this list who&apos;s still available. If nobody on it is left, your scouts take the best player available.</p>
                    {list.length === 0 ? <p className='meta'>Nobody yet. Add prospects from the table below.</p> :
                        <ol className='queue-list'>
                            {list.map((id, i) => {
                                const c = draft.prospects[id];
                                return (
                                    <li key={id}>
                                        <span className='queue-num'>{i + 1}</span>
                                        <span className='queue-name'><b>{name(c)}</b> <span className='meta'>{posText(c)} · age {c.age} · {c.overall}</span></span>
                                        <span className='queue-buttons'>
                                            <button className='icon-btn' aria-label={`Move ${name(c)} up`} disabled={i === 0} onClick={() => move(i, -1)}><FontAwesomeIcon icon={faArrowUp} /></button>
                                            <button className='icon-btn' aria-label={`Move ${name(c)} down`} disabled={i === list.length - 1} onClick={() => move(i, 1)}><FontAwesomeIcon icon={faArrowDown} /></button>
                                            <button className='icon-btn' aria-label={`Remove ${name(c)}`} onClick={() => edit(list.filter(x => x !== id))}><FontAwesomeIcon icon={faXmark} /></button>
                                        </span>
                                    </li>
                                );
                            })}
                        </ol>
                    }
                    {(dirty || saved.length > 0) && <div className='queue-actions'>
                        <button className={`g-button primary ${!dirty || busy ? 'disabled' : ''}`} disabled={!dirty || busy} onClick={save}>
                            {busy ? <ThreeDotsLoader /> : dirty ? 'Save queue' : 'Saved'}
                        </button>
                        {dirty && <button className='g-button' disabled={busy} onClick={() => setQueue(null)}>Undo changes</button>}
                    </div>}
                </div>
            }

            <div className='g-card'>
                <h3>Lottery</h3>
                <p className='meta'>The {draft.lottery.length} teams that missed the playoffs, worst record first, with their chances at the first pick. The top {LOTTERY_PICKS} picks were drawn; everyone else picks in reverse order of record.</p>
                <div className='table-scroll'>
                    <table className='g-table lottery-table'>
                        <thead>
                            <tr><th className='num'>Seed</th><th>Team</th><th className='num'>Odds</th><th className='num'>Pick</th></tr>
                        </thead>
                        <tbody>
                            {draft.lottery.map(l => (
                                <tr key={l.teamId} className={l.teamId === myId ? 'mine' : ''}>
                                    <td className='num'>{l.seed}</td>
                                    <td>{teamCell(l.teamId)}</td>
                                    <td className='num'>{pct(l.odds)}</td>
                                    <td className='num'>
                                        <b>{l.pick}</b>
                                        {l.pick < l.seed && <span className='moved up'> ▲{l.seed - l.pick}</span>}
                                        {l.pick > l.seed && <span className='moved down'> ▼{l.pick - l.seed}</span>}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <div className='g-card'>
                <h3>{draft.done ? 'The picks' : 'Draft order'}</h3>
                {Array.from({ length: DRAFT_ROUNDS }, (_, r) => (
                    <div key={r} className='draft-round'>
                        <h4>Round {r + 1}</h4>
                        <ol className='pick-list'>
                            {draft.order.filter(o => o.round === r + 1).map(o => {
                                const c = o.playerId && draft.prospects[o.playerId];
                                return (
                                    <li key={o.pick} className={o.teamId === myId ? 'mine' : ''}>
                                        <span className='pick-num'>{o.pick}</span>
                                        {teamCell(o.teamId)}
                                        {o.via && <span className='meta pick-via'>via {teams?.[o.via]?.abbr ?? o.via}</span>}
                                        {c &&
                                            <span className='pick-player'>
                                                {playerLink ? <Link to={playerLink(o.teamId, c.id)}>{name(c)}</Link> : name(c)}
                                                <span className='meta'> {posText(c)} · {c.age} · {c.overall}</span>
                                            </span>
                                        }
                                    </li>
                                );
                            })}
                        </ol>
                    </div>
                ))}
            </div>

            <div className='g-card'>
                <h3>Prospects</h3>
                <p className='meta'>{Object.keys(draft.prospects).length} players, ages 19 to 23. Their ratings are what scouts can see; young players usually have more growth ahead.</p>
                <div className='chips' role='tablist' aria-label='Position'>
                    {[0, 1, 2, 3, 4, 5].map(n => (
                        <button key={n} role='tab' aria-selected={pos === n} className={`chip ${pos === n ? 'active' : ''}`} onClick={() => setPos(n)}>{n ? positions[n - 1].short : 'All'}</button>
                    ))}
                </div>
                <div className='table-scroll'>
                    <table className='g-table prospect-table'>
                        <thead>
                            <tr>
                                {myTeam && open && <th aria-label='Queue'></th>}
                                {th('name', 'Player', 'asc', '')}
                                {th('pos', 'Pos', 'asc', '')}
                                {th('ovr', 'Ovr')}
                                {th('age', 'Age', 'asc')}
                                {th('ht', 'Ht')}
                                {COLUMNS.slice(1).map(col => <SortTh key={col.key} k={col.key} label={col.label} sorter={sorter} className='num' />)}
                                {draft.done && th('pick', 'Pick', 'asc')}
                            </tr>
                        </thead>
                        <tbody>
                            {prospects.map(c => {
                                const at = list.indexOf(c.id);
                                const pick = drafted.get(c.id);
                                return (
                                    <tr key={c.id} className={at >= 0 || pick?.teamId === myId ? 'mine' : ''}>
                                        {myTeam && open &&
                                            <td>
                                                {at >= 0 ?
                                                    <span className='queued' title='On your queue'>#{at + 1}</span> :
                                                    <button className='g-button small-btn' onClick={() => edit([...list, c.id])}>Add</button>}
                                            </td>
                                        }
                                        <td className='strong'>{name(c)}</td>
                                        <td>{posText(c)}</td>
                                        <td className='num strong'>{c.overall}</td>
                                        <td className='num'>{c.age}</td>
                                        <td className='num'>{height(c.height)}</td>
                                        {COLUMNS.slice(1).map(col => <td key={col.key} className='num'>{col.get(c)}</td>)}
                                        {draft.done && <td className='num'>{pick ? `#${pick.pick} ${teams?.[pick.teamId]?.abbr ?? ''}` : 'FA'}</td>}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            <ModalAlert show={alertMessage} cancel={() => setAlertMessage(null)} message={alertMessage} />
        </div>
    )
}

export default DraftView
